import React from 'react'

import { EditorState, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'

import { NodeTypeMap } from '@showrunner/codex'

import { blockInfoPluginKey } from '#choo-app/lib/editor/plugins/block-info'
import { docInfoPluginKey } from '#choo-app/lib/editor/plugins/doc-info'
import { ILoadedScript } from '#state'
import { BlockInfo } from '#util/constants'

import styles from './index.module.css'

export type ElementNumberInventory = BlockInfo[]

export const elementNumbersPluginKey = new PluginKey<ElementNumberInventory>(
  'elementNumbers',
)

export const getElementNumberInventory = (
  state: EditorState,
): ElementNumberInventory => {
  const blockInfo = blockInfoPluginKey.getState(state)
  const docInfo = docInfoPluginKey.getState(state)

  // besides displaying existing element numbers, we also need to display a placeholder
  // when we know that *other* elements are present in the document
  return (
    blockInfo?.filter((b) => docInfo?.elementNumbers[b.type]?.isNumbered) ?? []
  )
}

const gutterDecorations = ({
  inventory,
  script,
}: {
  inventory: ElementNumberInventory
  script: ILoadedScript
}): Decoration[] => {
  const onChange = (e: Event, id: string) => {
    const evt = e as unknown as React.ChangeEvent<HTMLInputElement>
    const { value } = evt.currentTarget
    script.updateElementNumber({ id, value })
  }

  const decorations: Decoration[] = []

  for (const block of inventory) {
    decorations.push(
      createElementNumberWidget({ block, side: 'start', onChange }),
    )

    if (block.type === NodeTypeMap.SCENE_HEADING) {
      decorations.push(
        createElementNumberWidget({ block, side: 'end', onChange }),
      )
    }
  }

  return decorations
}

export const getDecorations = ({
  state,
  script,
}: {
  script: ILoadedScript
  state: EditorState
}) => {
  const inventory = elementNumbersPluginKey.getState(state)
  if (!inventory) return DecorationSet.empty

  const decos = gutterDecorations({ inventory, script })
  return DecorationSet.create(state.doc, decos)
}

function createElementNumberWidget(props: {
  block: BlockInfo
  side: 'start' | 'end'
  onChange(event: Event, id: BlockInfo['id']): void
}) {
  return Decoration.widget(props.block.pos + 1, toDOM, {
    key: [props.block.id, props.block.elementNumber, props.side].join('-'),
    stopEvent: () => true,
    ignoreSelection: true,
    side: props.side === 'end' ? 1 : -1,
  })

  function toDOM() {
    const elt = document.createElement('input')
    elt.type = 'text'
    elt.innerHTML = ''
    elt.value = props.block.elementNumber ?? ''
    elt.placeholder = '#'
    elt.maxLength = 5
    elt.className = ['element-number-input', styles[props.side]].join(' ')
    elt.onchange = (e) => props.onChange(e, props.block.id)
    return elt
  }
}
